// frontend/src/components/Layout/Layout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import RoleSelectionModal from '../common/RoleSelectionModal';
import logo from '../../assets/logoproxy.png';
import {
  ArrowRightOnRectangleIcon,
  RocketLaunchIcon,
  XMarkIcon,
  Bars3Icon,
} from '@heroicons/react/24/solid';
import './Layout.css';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userType, setUserType] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [modalMode, setModalMode] = useState('register');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      setIsLoggedIn(true);
      try {
        const userData = JSON.parse(user);
        setUserType(userData.type || (userData.company_name ? 'partner' : 'client'));
      } catch (e) {
        console.error('Erreur parsing user');
      }
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUserType(null);
    navigate('/');
  };

  const getDashboardLink = () => {
    if (userType === 'partner') return '/dashboard/partner';
    if (userType === 'client') return '/dashboard/client';
    return '/login/partner';
  };

  const handleRoleSelect = (role) => {
    setShowRoleModal(false);
    
    if (modalMode === 'login') {
      // Mode CONNEXION
      if (role === 'partner') {
        navigate('/login/partner');
      } else if (role === 'client') {
        navigate('/login/client');
      }
    } else {
      // Mode INSCRIPTION
      if (role === 'partner') {
        navigate('/register/partner');
      } else if (role === 'client') {
        navigate('/register/client');
      }
    }
  };

  const handleRegisterClick = () => {
    setModalMode('register');
    setShowRoleModal(true);
  };

  const handleLoginClick = () => {
    setModalMode('login');
    setShowRoleModal(true);
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.querySelector(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="layout">
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src={logo} alt="POINTPAY" className="logo-img" />
          </div>

          <div className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}>
            <button className="nav-link" onClick={() => scrollToSection('#accueil')}>Accueil</button>
            <button className="nav-link" onClick={() => scrollToSection('#solution')}>Solution</button>
            <button className="nav-link" onClick={() => scrollToSection('#comment-ca-marche')}>Fonctionnement</button>
            <button className="nav-link" onClick={() => scrollToSection('#contact')}>Contact</button>
          </div>

          <div className="nav-buttons">
            {isLoggedIn ? (
              <>
                <motion.button
                  className="btn-dashboard"
                  onClick={() => navigate(getDashboardLink())}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Tableau de bord
                </motion.button>
                <motion.button
                  className="btn-logout"
                  onClick={handleLogout}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ArrowRightOnRectangleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                  Déconnexion
                </motion.button>
              </>
            ) : (
              <>
                <motion.button
                  className="btn-login"
                  onClick={handleLoginClick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Connexion
                </motion.button>
                <motion.button
                  className="btn-register"
                  onClick={handleRegisterClick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RocketLaunchIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                  S'inscrire
                </motion.button>
              </>
            )}
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen
              ? <XMarkIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              : <Bars3Icon style={{ width: 18, height: 18, flexShrink: 0 }} />
            }
          </button>
        </div>
      </nav>

      <main className="main-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="footer" id="contact">
        <div className="footer-container">
          <div className="footer-brand">
            <div className="footer-logo" onClick={() => navigate('/')}>
              <img src={logo} alt="POINTPAY" className="logo-img" />
            </div>
          </div>
          
          <div className="footer-links">
            <div className="footer-col">
              <h4>Plateformes</h4>
              <button className="footer-link" onClick={() => navigate('/login/client')}>Espace Client</button>
              <button className="footer-link" onClick={() => navigate('/login/partner')}>Espace Partenaire</button>
            </div>
            
            <div className="footer-col">
              <h4>Légal</h4>
              <button className="footer-link" onClick={(e) => e.preventDefault()}>CGU</button>
              <button className="footer-link" onClick={(e) => e.preventDefault()}>Confidentialité</button>
              <button className="footer-link" onClick={(e) => e.preventDefault()}>Mentions légales</button>
            </div>
            
            <div className="footer-col">
              <h4>Support</h4>
              <button className="footer-link" onClick={(e) => e.preventDefault()}>Contact</button>
              <button className="footer-link" onClick={(e) => e.preventDefault()}>FAQ</button>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <div>© 2026 POINTPAY - Tous droits réservés</div>
          <div className="footer-bottom-links">
            <button className="footer-link" onClick={(e) => e.preventDefault()}>CGU</button>
            <button className="footer-link" onClick={(e) => e.preventDefault()}>Confidentialité</button>
          </div>
        </div>
      </footer>

      {/* Modal de sélection Client/Partenaire */}
      <RoleSelectionModal 
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        onSelect={handleRoleSelect}
        mode={modalMode}
      />
    </div>
  );
};

export default Layout;